// Renders state diagnostics for the DEBUG tab: answer keys, table rows,
// selected rows, plus a button that runs all operations now (without the
// CALCULATE2 overlay) so you can inspect reactive script output.

import { useState } from "react";
import { useAnswers } from "../state/AnswersContext";

export default function DebugPanel() {
  const { answers, tables, selectedRows, runOps } = useAnswers();
  const [filter, setFilter] = useState("");
  const [lastRun, setLastRun] = useState<{
    ranNames: string[];
    failed: Array<{ name: string; error?: string }>;
  } | null>(null);

  const sortedKeys = Object.keys(answers).sort();
  const fLower = filter.trim().toLowerCase();
  const filtered = fLower
    ? sortedKeys.filter((k) => k.toLowerCase().includes(fLower))
    : sortedKeys;

  return (
    <section className="cpq-tabpanel cpq-debug-tab">
      <div className="cpq-debug-card">
        <header>Answer state ({sortedKeys.length} keys)</header>
        <input
          className="cpq-input"
          placeholder="filter keys…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <table className="cpq-table cpq-debug-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((k) => (
              <tr key={k}>
                <td>
                  <code>{k}</code>
                </td>
                <td>
                  <code>{stringify(answers[k])}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cpq-debug-card">
        <header>Tables ({Object.keys(tables).length})</header>
        {Object.entries(tables).map(([code, rows]) => (
          <details key={code}>
            <summary>
              <code>{code}</code> — {rows.length} row{rows.length === 1 ? "" : "s"}
            </summary>
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </details>
        ))}
      </div>

      <div className="cpq-debug-card">
        <header>Selected rows</header>
        <pre>{JSON.stringify(selectedRows, null, 2)}</pre>
      </div>

      <div className="cpq-debug-card">
        <header>Run operations now</header>
        <button
          type="button"
          className="cpq-btn cpq-btn-primary"
          onClick={() => {
            const r = runOps();
            setLastRun({
              ranNames: r.ran.filter((x) => x.ok).map((x) => x.name),
              failed: r.ran
                .filter((x) => !x.ok)
                .map((x) => ({ name: x.name, error: x.error })),
            });
          }}
        >
          Run all reactive ops
        </button>
        {lastRun && (
          <div className="cpq-debug-runresult">
            <p>
              <strong>Ran ({lastRun.ranNames.length}):</strong>
            </p>
            <pre>{lastRun.ranNames.join("\n")}</pre>
            {lastRun.failed.length > 0 && (
              <>
                <p>
                  <strong>Failed ({lastRun.failed.length}):</strong>
                </p>
                <pre>
                  {lastRun.failed
                    .map((f) => `${f.name}: ${f.error}`)
                    .join("\n")}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function stringify(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (typeof v === "string") return `"${v}"`;
  return String(v);
}
